import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { canLiftEviction } from "../lib/config";
import { formatDate } from "../lib/workorders";

export function EvictionListPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canLift = user ? canLiftEviction(user.role) : false;

  const [status, setStatus] = useState("active");
  const [liftingId, setLiftingId] = useState<number | null>(null);
  const [liftReason, setLiftReason] = useState("");

  const listQ = useQuery({
    queryKey: ["eviction-list", status],
    queryFn: () => api.evictionList.list(status === "all" ? "" : status),
  });

  const liftMut = useMutation({
    mutationFn: (id: number) => api.evictionList.lift(id, liftReason.trim()),
    onSuccess: () => {
      setLiftingId(null);
      setLiftReason("");
      queryClient.invalidateQueries({ queryKey: ["eviction-list"] });
    },
  });

  const entries = listQ.data?.entries ?? [];

  return (
    <Layout title="Eviction List">
      <p className="help-line">
        People here are blocked from becoming a tenant again at any building. Only a Manager can
        lift an entry. <Link to="/tenants">Back to Tenants</Link>.
      </p>

      <div className="filters">
        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="lifted">Lifted</option>
            <option value="all">All</option>
          </select>
        </label>
      </div>

      {listQ.isLoading ? (
        <p className="sub">Loading…</p>
      ) : listQ.isError ? (
        <section className="card">
          <p>Could not load the eviction list. Please try again.</p>
        </section>
      ) : entries.length === 0 ? (
        <section className="card">
          <p className="sub">No entries match this filter.</p>
        </section>
      ) : (
        entries.map((e) => (
          <section className="card" key={e.id}>
            <div className="detail-head">
              <div>
                <h2>
                  {e.firstName} {e.lastName}
                </h2>
                <p className="sub">Born {formatDate(e.dateOfBirth)}</p>
              </div>
              <span className={`badge badge-${e.status === "active" ? "alert" : "muted"}`}>
                {e.status === "active" ? "Active" : "Lifted"}
              </span>
            </div>
            <dl className="detail-list">
              <div>
                <dt>Evicted</dt>
                <dd>{formatDate(e.evictionDate)}</dd>
              </div>
              <div>
                <dt>Recorded at</dt>
                <dd>{e.originBuildingName ?? "—"}</dd>
              </div>
              <div>
                <dt>Recorded by</dt>
                <dd>{e.recordedByName}</dd>
              </div>
              <div>
                <dt>Reason</dt>
                <dd>{e.reason ?? "—"}</dd>
              </div>
              <div>
                <dt>Amount outstanding</dt>
                <dd>
                  {e.amountOutstanding == null
                    ? "—"
                    : e.amountOutstanding.toLocaleString("en-US", {
                        style: "currency",
                        currency: "CAD",
                      })}
                </dd>
              </div>
              <div>
                <dt>Summary</dt>
                <dd>{e.summary ?? "—"}</dd>
              </div>
              {e.status === "lifted" && (
                <div>
                  <dt>Lifted</dt>
                  <dd>{e.liftedReason ?? "—"}</dd>
                </div>
              )}
            </dl>

            {e.status === "active" && canLift && liftingId === e.id && (
              <>
                <label className="field">
                  <span>Reason for lifting (recorded)</span>
                  <textarea
                    rows={2}
                    value={liftReason}
                    onChange={(ev) => setLiftReason(ev.target.value)}
                    placeholder="e.g. Mistaken identity, or the matter has been resolved…"
                  />
                </label>
                {liftMut.isError && <p className="field-error">Could not lift. Try again.</p>}
                <div className="form-actions">
                  <button
                    className="btn btn-ghost btn-xl"
                    onClick={() => setLiftingId(null)}
                    disabled={liftMut.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-xl"
                    onClick={() => liftMut.mutate(e.id)}
                    disabled={liftMut.isPending || !liftReason.trim()}
                  >
                    {liftMut.isPending ? "Lifting…" : "Confirm lift"}
                  </button>
                </div>
              </>
            )}
            {e.status === "active" && canLift && liftingId !== e.id && (
              <div className="form-actions">
                <button
                  className="btn btn-ghost btn-xl"
                  onClick={() => {
                    setLiftingId(e.id);
                    setLiftReason("");
                  }}
                >
                  Lift this entry
                </button>
              </div>
            )}
            {e.status === "active" && !canLift && (
              <p className="sub">Only Management can lift an eviction entry.</p>
            )}
          </section>
        ))
      )}
    </Layout>
  );
}
